import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import KanbanLaneConfigModal from './KanbanLaneConfigModal';
import { Typography, Box, Checkbox, IconButton, Menu, MenuItem, Button, Popover, TextField } from '@material-ui/core';
import { MoreVert as MoreVertIcon, Add as AddIcon, Palette as PaletteIcon } from '@material-ui/icons';
import NewTicketModal from '../../components/NewTicketModal';
import { i18n } from '../../translate/i18n';
import api from '../../services/api';
import toastUtils from '../../utils/toastUtils';

const useStyles = makeStyles(theme => ({
  column: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    minWidth: 272,
    maxWidth: 272,
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
  },
  columnTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    margin: 0,
    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    maxHeight: 'calc(100vh - 200px)',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  totalValue: {
    fontSize: '0.9rem',
    fontWeight: 500,
    opacity: 0.9,
  },
  columnHeader: props => ({
    background: props.color ? `linear-gradient(135deg, ${props.color}, ${props.color}dd)` : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.getContrastText(props.color || theme.palette.primary.main),
    padding: theme.spacing(2),
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.05) 100%)',
      pointerEvents: 'none',
    },
  }),
  columnStats: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  taskCount: {
    fontSize: '0.85rem',
    fontWeight: 500,
    opacity: 0.8,
  },
  headerActions: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1,
  },
  menuButton: {
    color: 'inherit',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  addTaskButton: {
    margin: theme.spacing(1),
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  colorOption: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `2px solid transparent`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
      border: `2px solid ${theme.palette.text.primary}`,
    },
  },
  paletteButton: {
    color: 'inherit',
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
}));

// Cores predefinidas para as lanes
const PREDEFINED_COLORS = [
  { name: 'Padrão', value: '#757575' },
  { name: 'Azul', value: '#2196f3' },
  { name: 'Verde', value: '#4caf50' },
  { name: 'Amarelo', value: '#ff9800' },
  { name: 'Roxo', value: '#9c27b0' },
  { name: 'Rosa', value: '#e91e63' },
  { name: 'Laranja', value: '#ff5722' },
  { name: 'Ciano', value: '#00bcd4' },
  { name: 'Vermelho', value: '#f44336' },
  { name: 'Lime', value: '#8bc34a' },
  { name: 'Indigo', value: '#3f51b5' },
  { name: 'Teal', value: '#009688' },
];

const KanbanColumn = ({
  id, 
  title, 
  tickets, 
  color, 
  updateTicket, 
  selectedTasks = [], 
  onTaskSelection, 
  viewMode = 'board',
  onTaskCreated,
  onLaneUpdated
}) => {
  const classes = useStyles({ color });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showLaneConfig, setShowLaneConfig] = useState(false);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  
  const allTicketsSelected = tickets.length > 0 && tickets.every(ticket => 
    selectedTasks.includes(ticket.id)
  );
  
  const someTicketsSelected = tickets.some(ticket => 
    selectedTasks.includes(ticket.id)
  );
  
  const handleSelectAll = () => {
    if (onTaskSelection) {
      tickets.forEach(ticket => {
        onTaskSelection(ticket.id, !allTicketsSelected);
      });
    }
  };
  
  const handleMenuClick = (event) => {
    setMenuAnchor(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleColorClick = (event) => {
    setColorAnchor(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchor(null);
  };

  const handleColorChange = async (newColor) => {
    try {
      // Usar apenas os dados mínimos necessários
      await api.put(`/tags/${id}`, {
        name: title,
        color: newColor,
        kanban: 1
      });

      toastUtils.success(i18n.t('kanban.lane.colorUpdated'), 'Sucesso');
      handleColorClose();

      // Refresh lanes
      if (onLaneUpdated) onLaneUpdated();
    } catch (error) {
      console.error('Error updating lane color:', error);
      console.error('Error details:', error.response?.data);
      toastUtils.error(i18n.t('kanban.lane.updateColorError'), 'Erro');
    }
  };


  const handleNewTicketCreated = async (ticket) => {
    if (ticket && ticket.id) {
      try {
        // Associar o ticket à tag/lane atual
        await api.put(`/tickets/${ticket.id}/tags`, {
          tagIds: [id]
        });

        toastUtils.success('Ticket criado e associado à lane', 'Sucesso');

        // Refresh lanes
        if (onLaneUpdated) onLaneUpdated();
      } catch (error) {
        console.error('Error associating ticket to lane:', error);
        toastUtils.error('Erro ao associar ticket à lane', 'Erro');
      }
    }

    // Sempre fechar o modal, mesmo se não houver ticket (cancelamento)
    setNewTicketModalOpen(false);
  };

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  return (
    <Droppable droppableId={id}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          <div className={classes.columnHeader}>
            <div className={classes.headerActions}>
              <IconButton
                size="small"
                onClick={handleColorClick}
                className={classes.paletteButton}
                title={i18n.t('kanban.column.colorChange')}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleMenuClick}
                className={classes.menuButton}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </div>
            
            <Box display="flex" alignItems="center" mb={1}>
              {onTaskSelection && (
                <Checkbox
                  size="small"
                  checked={allTicketsSelected}
                  indeterminate={someTicketsSelected && !allTicketsSelected}
                  onChange={handleSelectAll}
                  style={{ 
                    padding: 4, 
                    marginRight: 8,
                    color: 'inherit'
                  }}
                />
              )}
              <Typography className={classes.columnTitle}>
                {title}
              </Typography>
            </Box>
            
            <div className={classes.columnStats}>
              <Typography className={classes.taskCount}>
                {tickets.length} {tickets.length === 1 ? i18n.t('kanban.column.itemCount') : i18n.t('kanban.column.itemsCount')}
              </Typography>
              <Typography className={classes.totalValue}>
                {i18n.t('kanban.column.totalValue')}: $ {totalValue.toFixed(2)}
              </Typography>
            </div>
          </div>
          <div className={classes.cardList}>
            {tickets.map((ticket, index) => (
              <KanbanCard
                key={ticket.id}
                ticket={ticket}
                index={index}
                updateTicket={updateTicket}
                isSelected={selectedTasks.includes(ticket.id)}
                onSelect={onTaskSelection}
                showCheckbox={!!onTaskSelection}
                viewMode={viewMode}
              />
            ))}
            {provided.placeholder}
            
            {/* Color Picker Popover */}
            <Popover
              anchorEl={colorAnchor}
              open={Boolean(colorAnchor)}
              onClose={handleColorClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box p={2} width={200}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t('kanban.column.selectColor')}
                </Typography>
                <div className={classes.colorGrid}>
                  {PREDEFINED_COLORS.map((colorOption) => (
                    <div
                      key={colorOption.value}
                      className={classes.colorOption}
                      style={{
                        backgroundColor: colorOption.value,
                        border: color === colorOption.value ? '2px solid #000' : '2px solid transparent'
                      }}
                      onClick={() => handleColorChange(colorOption.value)}
                      title={colorOption.name}
                    />
                  ))}
                </div>
              </Box>
            </Popover>

            {/* Column context menu */}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { handleSelectAll(); handleMenuClose(); }}>
                {allTicketsSelected ? i18n.t('kanban.bulk.deselectAll') : i18n.t('kanban.bulk.selectAll')}
              </MenuItem>
              <MenuItem onClick={() => { setNewTicketModalOpen(true); handleMenuClose(); }}>
                {i18n.t('kanban.column.addTicket')}
              </MenuItem>
              <MenuItem onClick={() => { setShowLaneConfig(true); handleMenuClose(); }}>
                {i18n.t('kanban.column.settings')}
              </MenuItem>
              <MenuItem onClick={() => { 
                // Criar tarefa para os tickets selecionados
                if (onTaskSelection && selectedTasks.length > 0) {
                  // Emitir evento para abrir o modal de criação de tarefa em lote
                  const event = new CustomEvent('openBulkTaskModal', {
                    detail: { selectedTickets: selectedTasks }
                  });
                  window.dispatchEvent(event);
                } else {
                  // Se não houver tickets selecionados, abrir modal para criar tarefa individual
                  setNewTicketModalOpen(true);
                }
                handleMenuClose(); 
              }}>
                {i18n.t('kanban.tasks.createTask')}
              </MenuItem>
            </Menu>
            
            <NewTicketModal
              modalOpen={newTicketModalOpen}
              onClose={handleNewTicketCreated}
            />
            
            <KanbanLaneConfigModal
              open={showLaneConfig}
              onClose={() => setShowLaneConfig(false)}
              laneId={id}
              laneTitle={title}
              laneColor={color}
              onLaneUpdated={() => {
                onLaneUpdated && onLaneUpdated();
                setShowLaneConfig(false);
              }}
            />
            
            {/* Add Ticket Button */}
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setNewTicketModalOpen(true)}
              className={classes.addTaskButton}
              fullWidth
            >
              {i18n.t('kanban.column.addTicket')}
            </Button>
            
            {provided.placeholder}
          </div>
        </div>
      )}
    </Droppable>
  );
};

export default KanbanColumn;